"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { trackEvent } from "@/lib/gtag";
import Icon from "@/components/vster/Icon";
import { c } from "@/lib/vster/theme";
import { TARGET_COMPANY_TYPES, TARGET_INDUSTRIES, PROFILE_REGIONS } from "@/src/docFields";

// ── 고용지원금 제도 카탈로그 (예상 금액 안내용 — 실제 지급은 담당기관 최신 공고 기준) ──
type Age = "만 34세 이하" | "35~49세" | "50세 이상";
type Employ = "신규 정규직" | "정규직 전환" | "인턴·계약직";
type Region = "서울" | "그 외 지역";
type Scheme = {
  id: string; name: string; cat: "인건비 지원" | "세액공제·훈련비"; agency: string; regionLabel: string;
  perPerson: number; note: string; tags: string[];
  ages: Age[]; employ: Employ[]; regions: Region[];
  req: string; // 한 줄 지원 요건(카드 표시용)
  applyNote: string; // 신청 시기/유의
  detail: string;
};

const CATALOG: Scheme[] = [
  { id: "youth-leap", name: "청년일자리도약장려금", cat: "인건비 지원", agency: "고용노동부", regionLabel: "전국",
    perPerson: 720, note: "월 60만원 × 12개월", tags: ["청년"],
    ages: ["만 34세 이하"], employ: ["신규 정규직"], regions: ["서울", "그 외 지역"],
    req: "만 15~34세 취업애로청년을 정규직으로 6개월 이상 고용", applyNote: "채용 후 3개월 내 참여 신청 필요",
    detail: "만 15~34세 청년을 정규직으로 신규 채용해 6개월 이상 고용 유지한 우선지원대상기업. 월 60만원씩 최대 12개월 지원." },
  { id: "seoul-youth-reg", name: "서울 청년 정규직 전환 지원", cat: "인건비 지원", agency: "서울산업진흥원", regionLabel: "서울",
    perPerson: 1200, note: "월 100만원 × 12개월", tags: ["청년", "정규직 전환", "서울 전용"],
    ages: ["만 34세 이하"], employ: ["정규직 전환"], regions: ["서울"],
    req: "서울 소재 기업이 청년을 정규직으로 전환 시", applyNote: "전환일 기준 분기별 신청",
    detail: "서울 소재 기업이 청년을 정규직으로 전환할 경우 월 100만원씩 최대 12개월 인건비 지원." },
  { id: "job-create", name: "고용창출장려금 (일자리 함께하기)", cat: "인건비 지원", agency: "고용노동부", regionLabel: "전국",
    perPerson: 480, note: "월 40만원 × 12개월", tags: ["정규직 전환"],
    ages: ["만 34세 이하", "35~49세", "50세 이상"], employ: ["신규 정규직", "정규직 전환"], regions: ["서울", "그 외 지역"],
    req: "교대제 개편·근로시간 단축으로 신규 고용 창출", applyNote: "우선지원대상기업 기준",
    detail: "교대제 개편·실근로시간 단축 등으로 신규 인력을 정규직 채용한 기업. 1인당 월 40만원 최대 12개월." },
  { id: "senior-job", name: "신중년 적합직무 고용지원금", cat: "인건비 지원", agency: "고용노동부", regionLabel: "전국",
    perPerson: 960, note: "월 80만원 × 12개월", tags: ["중장년·신중년"],
    ages: ["50세 이상"], employ: ["신규 정규직"], regions: ["서울", "그 외 지역"],
    req: "만 50세 이상 구직자를 신중년 적합직무에 신규 채용", applyNote: "채용 후 3개월 내 신청",
    detail: "만 50세 이상 구직자를 신중년 적합직무에 신규 채용한 우선지원대상기업. 월 80만원 최대 12개월." },
  { id: "career-woman-tax", name: "경력단절여성 고용 세액공제", cat: "세액공제·훈련비", agency: "국세청", regionLabel: "전국",
    perPerson: 1500, note: "일시 지급 · 한도 적용", tags: ["세액공제·훈련비"],
    ages: ["만 34세 이하", "35~49세", "50세 이상"], employ: ["신규 정규직", "정규직 전환"], regions: ["서울", "그 외 지역"],
    req: "경력단절 여성을 재고용한 중소·중견기업", applyNote: "법인세·소득세 신고 시 공제",
    detail: "경력단절 여성을 재고용한 중소·중견기업에 인건비의 일정 비율을 세액공제. 한도 내 적용." },
  { id: "youth-train", name: "중소기업 청년 재직자 훈련비", cat: "세액공제·훈련비", agency: "중소벤처기업진흥공단", regionLabel: "전국",
    perPerson: 200, note: "일시 지급 · 한도 적용", tags: ["청년", "세액공제·훈련비"],
    ages: ["만 34세 이하"], employ: ["신규 정규직", "정규직 전환", "인턴·계약직"], regions: ["서울", "그 외 지역"],
    req: "청년 재직자 직무능력 향상 훈련 실시", applyNote: "훈련 과정 수료 후 정산",
    detail: "청년 재직자의 직무능력 향상 훈련비를 지원. 한도 내 일시 지급." },
];

const AGES: Age[] = ["만 34세 이하", "35~49세", "50세 이상"];
const EMPLOYS: Employ[] = ["신규 정규직", "정규직 전환", "인턴·계약직"];
const REGIONS: Region[] = ["서울", "그 외 지역"];
const CAT_FILTERS = ["전체", "청년", "중장년·신중년", "정규직 전환", "세액공제·훈련비", "서울 전용"] as const;
const HIRE_WHEN = ["1개월 내", "3개월 내", "6개월 내", "시기 미정"] as const;

// 만원 → "N억 N,NNN만원" 표기
function won(manwon: number): string {
  if (manwon >= 10000) {
    const eok = Math.floor(manwon / 10000);
    const rest = manwon % 10000;
    return rest ? `${eok}억 ${rest.toLocaleString()}만원` : `${eok}억원`;
  }
  return `${manwon.toLocaleString()}만원`;
}

const TAB_HREF = { calc: "/jobs", catalog: "/jobs/catalog" } as const;

// 카탈로그 실데이터 항목 (인력·고용 공고문). /jobs/catalog 에서 주입.
export type CatalogItem = {
  id: number; title: string; agency: string | null; region: string | null;
  cat2: string | null; amount: string | null; deadline: string | null; summary: string | null; closed: boolean;
};

// 로그인 사용자 기업 프로필 (계산기·문의에 반영).
export type JobsCompany = { name: string; companyType: string | null; industry: string | null; region: string | null; headcount: string | null; bizNo: string | null } | null;
const HEADCOUNTS = ["5인 미만", "5~9인", "10~29인", "30~99인", "100인 이상"] as const;

function regionToOption(region: string | null | undefined): Region {
  return region && /서울/.test(region) ? "서울" : "그 외 지역";
}

export default function JobsCalculator({ initialTab = "calc", catalogItems, company: companyProp = null, programs = [], loggedIn = false, initialCalc = null, embedded = false }: { initialTab?: "calc" | "catalog"; catalogItems?: CatalogItem[]; company?: JobsCompany; programs?: CatalogItem[]; loggedIn?: boolean; initialCalc?: { age?: string; employ?: string; region?: string; count?: string } | null; embedded?: boolean }) {
  // 탭은 라우트(path)로 결정 — /jobs(계산기), /jobs/catalog(카탈로그). 새로고침해도 경로가 탭을 유지.
  const tab = initialTab;
  const router = useRouter();
  // URL 쿼리(?age=&employ=&region=&count=)로 넘어온 검색 조건 — 유효하면 초기 상태·결과에 반영(공유·새로고침·뒤로가기 지원).
  const qAge = (AGES as string[]).includes(initialCalc?.age ?? "") ? (initialCalc!.age as Age) : null;
  const qEmploy = (EMPLOYS as string[]).includes(initialCalc?.employ ?? "") ? (initialCalc!.employ as Employ) : null;
  const qRegion = (REGIONS as string[]).includes(initialCalc?.region ?? "") ? (initialCalc!.region as Region) : null;
  const qCount = Math.max(1, Math.min(99, Number(initialCalc?.count) || 0));
  const hasQuery = !!qAge; // age 파라미터가 유효하면 '검색됨'으로 간주(결과 표시)
  // 회사 프로필(초기 서버값 → 편집 시 갱신). 없으면 계산 전에 프로필 입력 모달을 띄운다.
  const [company, setCompany] = useState<JobsCompany>(companyProp);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pfCompany, setPfCompany] = useState(company?.name ?? "");
  const [pfType, setPfType] = useState<string>(company?.companyType ?? TARGET_COMPANY_TYPES[0]);
  const [pfIndustry, setPfIndustry] = useState<string>(company?.industry ?? TARGET_INDUSTRIES[0]);
  const [pfRegion, setPfRegion] = useState<string>(company?.region ?? "서울");
  const [pfHeadcount, setPfHeadcount] = useState<string>(company?.headcount ?? HEADCOUNTS[0]);
  const [pfBizNo, setPfBizNo] = useState(company?.bizNo ?? "");
  const [pfSaving, setPfSaving] = useState(false);
  const [pfErr, setPfErr] = useState<string | null>(null);
  // 계산기 조건 (URL 쿼리 > 기업 프로필 소재지 > 기본값 순으로 초기화)
  const [age, setAge] = useState<Age>(qAge ?? "만 34세 이하");
  const [employ, setEmploy] = useState<Employ>(qEmploy ?? "신규 정규직");
  const [region, setRegion] = useState<Region>(qRegion ?? regionToOption(company?.region));
  const [count, setCount] = useState(hasQuery && qCount ? qCount : 1);
  // URL 에 검색 조건이 있으면 결과를 바로 표시(라우팅 기반 검색 결과).
  const [result, setResult] = useState<Scheme[] | null>(
    hasQuery
      ? CATALOG.filter((s) => s.ages.includes(qAge!) && s.employ.includes(qEmploy ?? "신규 정규직") && s.regions.includes(qRegion ?? regionToOption(company?.region)))
      : null,
  );
  const [added, setAdded] = useState(false); // '채용 계획에 추가' 클릭 표시
  const [openReq, setOpenReq] = useState<string | null>(null); // 카드 '지원 요건 보기' 토글
  // 노무사 신청 대행 문의 모달
  const [inquiry, setInquiry] = useState<{ scheme?: string } | null>(null);
  const [contact, setContact] = useState("");
  const [bizNo, setBizNo] = useState(company?.bizNo ?? "");
  const [hireWhen, setHireWhen] = useState<(typeof HIRE_WHEN)[number]>("1개월 내");
  const [memo, setMemo] = useState("");
  const [sent, setSent] = useState(false);
  // 카탈로그 필터
  const [cf, setCf] = useState<(typeof CAT_FILTERS)[number]>("전체");
  const [open, setOpen] = useState<string | null>(null);

  const applicable = useMemo(
    () => CATALOG.filter((s) => s.ages.includes(age) && s.employ.includes(employ) && s.regions.includes(region)),
    [age, employ, region],
  );
  const catalogShown = useMemo(
    () => (cf === "전체" ? CATALOG : CATALOG.filter((s) => s.tags.includes(cf))),
    [cf],
  );

  // 실데이터 카탈로그(/jobs/catalog)
  const usingReal = !!catalogItems;
  const realCats = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of catalogItems ?? []) if (it.cat2) m.set(it.cat2, (m.get(it.cat2) ?? 0) + 1);
    return ["전체", ...[...m.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k)];
  }, [catalogItems]);
  const realShown = useMemo(
    () => (cf === "전체" ? (catalogItems ?? []) : (catalogItems ?? []).filter((it) => it.cat2 === cf)),
    [catalogItems, cf],
  );

  const total = result ? result.reduce((s, x) => s + x.perPerson, 0) * count : 0;

  // 스타일 헬퍼
  const chip = (on: boolean): React.CSSProperties => ({
    fontFamily: "inherit", fontSize: 14, fontWeight: on ? 700 : 500, cursor: "pointer",
    padding: "9px 16px", borderRadius: 999,
    background: on ? c.ink : c.surface, color: on ? "#fff" : c.muted,
    border: `1px solid ${on ? c.ink : c.line}`,
  });
  const fieldLabel: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: c.ink, margin: "0 0 8px" };
  const pfInput: React.CSSProperties = { width: "100%", boxSizing: "border-box", border: `1px solid ${c.line}`, borderRadius: 10, padding: "11px 13px", fontSize: 14, fontFamily: "inherit", background: c.surface };

  // 검색 조건을 URL 쿼리로 반영(라우터 생성) → 공유·새로고침·뒤로가기 지원.
  //   공백은 %20 으로 인코딩(encodeURIComponent) — `+` 로 나가면 Next 가 공백으로 안 풀어 매칭 실패하므로.
  const pushQuery = (reg: Region = region) => {
    const qs = [["age", age], ["employ", employ], ["region", reg], ["count", String(count)]]
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
    router.push(`/jobs?${qs}` as never, { scroll: false });
  };
  const doCalc = (reg: Region = region) => {
    setResult(CATALOG.filter((s) => s.ages.includes(age) && s.employ.includes(employ) && s.regions.includes(reg)));
    setAdded(false); setOpenReq(null);
    pushQuery(reg); // 결과 표시 + URL 갱신
  };
  // 계산하기: 회사 프로필이 없으면 먼저 입력 모달을 띄운다.
  const runCalc = () => { if (!company) { setProfileOpen(true); return; } doCalc(); };
  const resetAll = () => { setAge("만 34세 이하"); setEmploy("신규 정규직"); setRegion(regionToOption(company?.region)); setCount(1); setResult(null); setAdded(false); router.push("/jobs" as never, { scroll: false }); };
  const openInquiry = (scheme?: string) => { setInquiry({ scheme }); setSent(false); };

  // 프로필 저장 → (로그인 시) 서버 저장 + 계산 실행.
  const saveProfile = async () => {
    if (!pfCompany.trim()) { setPfErr("회사명을 입력하세요."); return; }
    setPfSaving(true); setPfErr(null);
    const next: JobsCompany = { name: pfCompany.trim(), companyType: pfType, industry: pfIndustry, region: pfRegion, headcount: pfHeadcount, bizNo: pfBizNo.trim() || null };
    if (loggedIn) {
      try {
        await fetch("/api/my/profile", { method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ company: next.name, companyType: pfType, industry: pfIndustry, region: pfRegion, headcount: pfHeadcount, bizNo: next.bizNo }) });
      } catch { /* 저장 실패해도 계산은 진행 */ }
    }
    const reg = regionToOption(pfRegion);
    setCompany(next); setRegion(reg); setBizNo(next.bizNo ?? "");
    setPfSaving(false); setProfileOpen(false);
    doCalc(reg);
  };

  // 노무사 문의 저장.
  const submitInquiry = async () => {
    if (!contact.trim()) return;
    try {
      await fetch("/api/jobs/inquiry", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact, bizNo, hireWhen, memo,
          ageCond: age, employCond: employ, regionCond: region, headcount: count,
          scheme: inquiry?.scheme ?? null,
          estimatedAmount: (result ?? applicable).reduce((s, x) => s + x.perPerson, 0) * count,
          companyName: company?.name ?? null, companyIndustry: company?.industry ?? null, companyRegion: company?.region ?? null,
        }) });
    } catch { /* 네트워크 실패해도 접수 안내 */ }
    trackEvent("generate_lead", { form: "jobs_inquiry", value: (result ?? applicable).reduce((s, x) => s + x.perPerson, 0) * count }); // 전환: 노무사 문의
    setSent(true);
  };

  // 왼쪽 채용 조건(연령·고용형태·소재지)에 맞는 채용·고용지원 사업(진행중, category1Sub=인력·고용).
  //   제목·요약·분류 텍스트에서 조건 키워드를 매칭·정렬. 연령이 정면 충돌하는 공고(예: 청년 선택인데 신중년 전용)는 제외.
  const matchedPrograms = useMemo(() => {
    const AGE_KW: Record<Age, RegExp> = {
      "만 34세 이하": /청년|만\s*3[0-4]\s*세|만\s*2\d\s*세|만\s*39\s*세|미취업\s*청년|영\s*플러스|일자리\s*도약|첫\s*일자리/,
      "35~49세": /중장년|경력\s*형|재직자|경력\s*단절/,
      "50세 이상": /신중년|중장년|장년|고령|50\s*세\s*이상|5060|시니어/,
    };
    const AGE_CONFLICT: Record<Age, RegExp | null> = {
      "만 34세 이하": /신중년|고령자|50\s*세\s*이상|시니어/,
      "35~49세": null,
      "50세 이상": /(^|[^가-힣])청년/,
    };
    const EMPLOY_KW: Record<Employ, RegExp> = {
      "신규 정규직": /신규|채용|정규직|고용\s*창출|일자리\s*창출|일자리\s*도약/,
      "정규직 전환": /전환|무기\s*계약|정규직\s*전환/,
      "인턴·계약직": /인턴|일\s*경험|일경험|직무\s*체험|현장\s*실습|계약직/,
    };
    const ay = AGE_KW[age], ac = AGE_CONFLICT[age], ey = EMPLOY_KW[employ];
    const scored = programs
      .map((p) => {
        const hay = `${p.title} ${p.summary ?? ""} ${p.cat2 ?? ""} ${p.agency ?? ""}`;
        const r = p.region ?? "";
        const regionOk = region === "서울" ? (!r || /서울|전국/.test(r)) : (!r || !/서울/.test(r));
        if (!regionOk) return null;
        if (ac && ac.test(hay)) return null; // 연령 정면 충돌 공고 제외
        const reasons: string[] = [];
        let score = 0;
        if (ay.test(hay)) { score += 3; reasons.push(age); }
        if (ey.test(hay)) { score += 2; reasons.push(employ); }
        if (r && (region === "서울" ? /서울/.test(r) : (!/전국/.test(r) && !/서울/.test(r)))) { score += 1; reasons.push(region); }
        else if (/전국/.test(r)) reasons.push("전국");
        return { p, score, reasons };
      })
      .filter((x): x is { p: CatalogItem; score: number; reasons: string[] } => x != null);
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 12);
  }, [programs, age, employ, region]);

  const tabBtn = (key: "calc" | "catalog", icon: string, label: string) => {
    const on = tab === key;
    return (
      <Link href={TAB_HREF[key] as never} style={{
        textDecoration: "none",
        display: "inline-flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: on ? 700 : 600,
        color: on ? c.actionDark : c.muted, padding: "12px 4px", marginBottom: -1.5,
        borderBottom: on ? `2.5px solid ${c.action}` : "2.5px solid transparent",
      }}>
        <Icon name={icon} size={18} color={on ? c.actionDark : c.muted} />{label}
      </Link>
    );
  };

  return (
    <main style={{ background: embedded ? "transparent" : c.bg, color: c.ink, animation: embedded ? undefined : "vfade .35s ease" }}>
      {/* ── 히어로 (embedded 랜딩에선 상위 섹션이 대신하므로 숨김) ── */}
      {!embedded && (
      <section style={{ background: c.surface, borderBottom: `1px solid ${c.line}` }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "56px 24px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10, color: c.actionDark, fontSize: 13, fontWeight: 700, letterSpacing: ".04em", marginBottom: 16 }}>
            <span style={{ width: 20, height: 2, background: c.actionDark, borderRadius: 2 }} />채용·고용지원
          </span>
          <h1 className="vh1" style={{ fontSize: 44, lineHeight: 1.15, letterSpacing: "-.03em", fontWeight: 800, margin: "0 0 14px" }}>
            이 사람 뽑으면,<br />지원금 얼마일까?
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: c.muted, margin: 0, maxWidth: 620 }}>
            채용 조건 3가지만 고르면 받을 수 있는 고용지원금을 한 번에 계산해드립니다
          </p>
        </div>
      </section>
      )}

      {/* ── 탭 ── */}
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, borderBottom: `1.5px solid ${c.line}`, marginTop: 28 }}>
          {tabBtn("calc", "calculate", "지원금 계산기")}
          {tabBtn("catalog", "list", "제도 카탈로그")}
        </div>
      </div>

      {/* ── 탭1: 지원금 계산기 ── */}
      {tab === "calc" && (
        <section style={{ maxWidth: 1120, margin: "0 auto", padding: "28px 24px 64px", display: "grid", gridTemplateColumns: "minmax(300px,360px) minmax(0,700px)", gap: 24, alignItems: "start" }} className="jc-grid">
          {/* 좌: 채용 조건 */}
          <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: 22, boxShadow: "0 4px 16px -14px rgba(14,23,38,.14)" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>채용 조건</h2>
            </div>

            {/* 기업 프로필 반영 배지 */}
            <p style={fieldLabel}>연령</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {AGES.map((a) => <button key={a} type="button" onClick={() => { setAge(a); setResult(null); }} style={chip(age === a)}>{a}</button>)}
            </div>

            <p style={fieldLabel}>고용 형태</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {EMPLOYS.map((e) => <button key={e} type="button" onClick={() => { setEmploy(e); setResult(null); }} style={chip(employ === e)}>{e}</button>)}
            </div>

            <p style={fieldLabel}>사업장 소재지</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {REGIONS.map((r) => <button key={r} type="button" onClick={() => { setRegion(r); setResult(null); }} style={chip(region === r)}>{r}</button>)}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${c.line}`, paddingTop: 18, marginBottom: 18 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>채용 인원</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button type="button" onClick={() => setCount((n) => Math.max(1, n - 1))} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, cursor: "pointer", fontSize: 18, color: c.ink }}>−</button>
                <span className="num" style={{ minWidth: 44, textAlign: "center", fontSize: 15, fontWeight: 700 }}>{count}명</span>
                <button type="button" onClick={() => setCount((n) => Math.min(99, n + 1))} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, cursor: "pointer", fontSize: 18, color: c.ink }}>+</button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={runCalc} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: c.action, color: "#fff", border: "none", borderRadius: 10, padding: "13px", fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}>
                <Icon name="calculate" size={18} color="#fff" />지원금 계산하기
              </button>
              <button type="button" onClick={resetAll} style={{ background: c.surface, color: c.muted, border: `1px solid ${c.line}`, borderRadius: 10, padding: "13px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>초기화</button>
            </div>
          </div>

          {/* 우: 결과 */}
          <div>
            {!result ? (
              <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: 26, minHeight: 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 12, boxShadow: "0 4px 16px -14px rgba(14,23,38,.14)" }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: c.actionSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="calculate" size={28} color={c.action} fill={1} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>채용 조건을 고르고 계산해 보세요</h3>
                <p style={{ fontSize: 14, color: c.muted, margin: 0, lineHeight: 1.6, maxWidth: 420 }}>연령·고용형태·소재지와 인원을 선택하면 받을 수 있는 고용지원금을 합산해 보여드립니다.</p>
                <button type="button" onClick={runCalc} style={{ marginTop: 6, background: c.action, color: "#fff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}>현재 조건으로 계산하기</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* 예상 지원금 합계 (다크 히어로) */}
                <div style={{ background: "linear-gradient(135deg,#1a2a44 0%,#0e1726 60%)", color: "#fff", borderRadius: 16, padding: "24px 26px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#9fb0cc", marginBottom: 6 }}>예상 지원금 합계</div>
                      <div className="num" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1 }}>
                        {result.length ? won(total) : "해당 제도 없음"}
                      </div>
                      {result.length > 0 && <div style={{ fontSize: 13, color: "#aeb9cc", marginTop: 10 }}>해당 제도 {result.length}건 · 채용 {count}명 기준</div>}
                    </div>
                    {result.length > 0 && (
                      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                        <button type="button" onClick={() => setAdded(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.10)", color: "#fff", border: "1px solid rgba(255,255,255,.22)", borderRadius: 10, padding: "10px 14px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                          <Icon name={added ? "event_available" : "calendar_month"} size={17} color="#fff" />{added ? "추가됨" : "채용 계획에 추가"}
                        </button>
                        <button type="button" onClick={() => openInquiry()} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: c.ink, border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                          <Icon name="support_agent" size={17} color={c.ink} />노무사에게 신청 문의
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#8b99b3", marginTop: 16, lineHeight: 1.5 }}>
                    요건을 모두 충족했을 때의 최대 금액이며, 실제 지급액은 심사 결과에 따라 달라집니다.
                  </div>
                </div>

                {/* 적용 제도 카드 (해당 없으면 카드 미표시 — 합계 히어로의 '해당 제도 없음'으로 안내) */}
                {result.length > 0 &&
                  result.map((s) => {
                    const isOpen = openReq === s.id;
                    return (
                      <div key={s.id} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: "20px 22px", boxShadow: "0 4px 16px -16px rgba(14,23,38,.14)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 9 }}>
                              <span style={{ fontSize: 11.5, fontWeight: 700, color: s.cat === "인건비 지원" ? c.actionDark : "#7c3aed", background: s.cat === "인건비 지원" ? c.actionSoft : "#f0e9ff", borderRadius: 6, padding: "3px 9px" }}>{s.cat}</span>
                              <span style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, background: c.surfaceAlt, borderRadius: 6, padding: "3px 9px" }}>{s.agency}</span>
                            </div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: c.ink, lineHeight: 1.3 }}>{s.name}</div>
                            <div style={{ fontSize: 13.5, color: c.muted, marginTop: 6, lineHeight: 1.5 }}>{s.req}</div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div className="num" style={{ fontSize: 24, fontWeight: 800, color: c.ink, letterSpacing: "-.02em" }}>{won(s.perPerson * count)}</div>
                            <div style={{ fontSize: 12, color: c.muted, marginTop: 3 }}>{s.note} × {count}명</div>
                          </div>
                        </div>
                        {isOpen && (
                          <div style={{ background: c.surfaceAlt, borderRadius: 10, padding: "13px 15px", margin: "14px 0 0" }}>
                            <p style={{ fontSize: 13.5, color: c.ink, lineHeight: 1.65, margin: 0 }}>{s.detail}</p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                              {s.tags.map((t) => <span key={t} style={{ fontSize: 11.5, fontWeight: 600, color: c.actionDark, background: c.actionSoft, borderRadius: 999, padding: "3px 10px" }}>#{t}</span>)}
                            </div>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, borderTop: `1px solid ${c.line}`, marginTop: 16, paddingTop: 14, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12.5, color: c.muted }}>{s.applyNote}</span>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={() => openInquiry(s.name)} style={{ fontSize: 13, fontWeight: 700, color: c.actionDark, background: c.actionSoft, border: `1px solid ${c.actionBorder}`, borderRadius: 8, padding: "8px 13px", cursor: "pointer" }}>신청 대행 문의</button>
                            <button type="button" onClick={() => setOpenReq(isOpen ? null : s.id)} style={{ fontSize: 13, fontWeight: 700, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 8, padding: "8px 13px", cursor: "pointer" }}>{isOpen ? "요건 접기" : "지원 요건 보기"}</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {/* 왼쪽 조건에 맞는 채용·고용지원 사업(실데이터, 진행중) */}
                <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: "20px 22px", boxShadow: "0 4px 16px -16px rgba(14,23,38,.14)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
                    <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>선택 조건에 맞는 채용·고용지원 사업 {matchedPrograms.length > 0 && <span style={{ color: c.actionDark }}>{matchedPrograms.length}</span>}</h3>
                    <Link href={"/jobs/catalog" as never} style={{ fontSize: 13, fontWeight: 700, color: c.action, textDecoration: "none" }}>전체 보기 →</Link>
                  </div>
                  <div style={{ fontSize: 12.5, color: c.muted, marginBottom: 12 }}>
                    {age} · {employ} · {region} 조건으로 진행중 인력·고용 지원사업을 매칭했습니다.
                  </div>
                  {matchedPrograms.length === 0 ? (
                    <p style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.6, margin: 0 }}>
                      선택한 조건에 맞는 진행중 인력·고용 지원사업을 찾지 못했습니다. 조건을 바꾸거나 <Link href={"/jobs/catalog" as never} style={{ color: c.action, fontWeight: 700, textDecoration: "none" }}>전체 카탈로그</Link>를 확인하세요.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {matchedPrograms.map(({ p, reasons }) => (
                        <Link key={p.id} href={`/programs/${p.id}` as never} className="jc-link-row" style={{ textDecoration: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 14px", background: c.surface }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{[p.agency, p.region, p.cat2].filter(Boolean).join(" · ")}</div>
                            {reasons.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                                {reasons.map((rz) => (
                                  <span key={rz} style={{ fontSize: 11, fontWeight: 700, color: c.actionDark, background: c.actionSoft, borderRadius: 999, padding: "2px 8px" }}>{rz} 부합</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            <div style={{ textAlign: "right" }}>
                              {p.amount && <div className="num" style={{ fontSize: 13, fontWeight: 800, color: c.actionDark, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.amount}</div>}
                              {p.deadline && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>~ {p.deadline}</div>}
                            </div>
                            <Icon name="chevron_right" size={18} color={c.muted} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <p style={{ fontSize: 12, color: c.muted, lineHeight: 1.6, margin: 0 }}>예상 금액 안내이며 요건 충족 시 지급됩니다. 중복수혜 제한·한도가 있을 수 있어 실제 수령액과 다를 수 있습니다.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 회사 프로필 입력 모달 (계산 전 프로필 없을 때) ── */}
      {profileOpen && (
        <div onClick={() => setProfileOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(14,23,38,.5)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: c.surface, borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 24px 60px -20px rgba(14,23,38,.4)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", borderBottom: `1px solid ${c.line}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 800 }}><Icon name="apartment" size={20} color={c.ink} />회사 프로필 입력</div>
              <button type="button" onClick={() => setProfileOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: c.muted, fontSize: 20, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px" }}>
              <p style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.6, margin: "0 0 16px" }}>정확한 맞춤 계산을 위해 회사 정보를 입력해 주세요.{loggedIn ? " 입력하면 프로필에 저장됩니다." : " (로그인하면 다음에 자동 반영됩니다)"}</p>
              {([
                ["회사명", <input key="c" value={pfCompany} onChange={(e) => setPfCompany(e.target.value)} placeholder="예: 정원테크" style={pfInput} />],
                ["기업 형태", <select key="t" value={pfType} onChange={(e) => setPfType(e.target.value)} style={pfInput}>{TARGET_COMPANY_TYPES.map((o) => <option key={o}>{o}</option>)}</select>],
                ["업종", <select key="i" value={pfIndustry} onChange={(e) => setPfIndustry(e.target.value)} style={pfInput}>{TARGET_INDUSTRIES.map((o) => <option key={o}>{o}</option>)}</select>],
                ["소재지", <select key="r" value={pfRegion} onChange={(e) => setPfRegion(e.target.value)} style={pfInput}>{PROFILE_REGIONS.map((o) => <option key={o}>{o}</option>)}</select>],
                ["상시 근로자 수", <select key="h" value={pfHeadcount} onChange={(e) => setPfHeadcount(e.target.value)} style={pfInput}>{HEADCOUNTS.map((o) => <option key={o}>{o}</option>)}</select>],
                ["사업자등록번호 (선택)", <input key="b" value={pfBizNo} onChange={(e) => setPfBizNo(e.target.value)} placeholder="000-00-00000" style={pfInput} />],
              ] as [string, React.ReactNode][]).map(([label, node]) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 6 }}>{label}</label>
                  {node}
                </div>
              ))}
              {pfErr && <p style={{ fontSize: 13, color: c.danger, margin: "0 0 10px" }}>{pfErr}</p>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
                <button type="button" onClick={() => setProfileOpen(false)} style={{ background: c.surface, color: c.muted, border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>취소</button>
                <button type="button" disabled={pfSaving} onClick={saveProfile} style={{ background: c.action, color: "#fff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 14.5, fontWeight: 700, cursor: "pointer", opacity: pfSaving ? 0.6 : 1 }}>{pfSaving ? "저장 중…" : "저장하고 계산하기"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 노무사 신청 대행 문의 모달 ── */}
      {inquiry && (
        <div onClick={() => setInquiry(null)} style={{ position: "fixed", inset: 0, background: "rgba(14,23,38,.5)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: c.surface, borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 24px 60px -20px rgba(14,23,38,.4)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", borderBottom: `1px solid ${c.line}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 800 }}>
                <Icon name="support_agent" size={20} color={c.ink} />노무사 신청 대행 문의
              </div>
              <button type="button" onClick={() => setInquiry(null)} style={{ background: "none", border: "none", cursor: "pointer", color: c.muted, fontSize: 20, lineHeight: 1 }}>✕</button>
            </div>

            {sent ? (
              <div style={{ padding: "40px 22px", textAlign: "center" }}>
                <div style={{ width: 54, height: 54, borderRadius: "50%", background: "#EAF7EF", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                  <Icon name="check_circle" size={30} color="#22A45D" fill={1} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, margin: "0 0 8px" }}>문의가 접수되었습니다</h3>
                <p style={{ fontSize: 14, color: c.muted, lineHeight: 1.6, margin: "0 0 20px" }}>제휴 노무사에게 전달되었습니다. 영업일 1일 내 회신드립니다.</p>
                <button type="button" onClick={() => setInquiry(null)} style={{ background: c.action, color: "#fff", border: "none", borderRadius: 10, padding: "12px 26px", fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}>닫기</button>
              </div>
            ) : (
              <div style={{ padding: "20px 22px" }}>
                {/* 함께 전달되는 정보 */}
                <div style={{ background: c.surfaceAlt, borderRadius: 12, padding: "14px 16px", marginBottom: 18 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: c.muted, marginBottom: 10 }}>문의에 함께 전달되는 정보</div>
                  {[
                    ["채용 조건", `${age} · ${employ} · ${region} · ${count}명`],
                    ["해당 제도", inquiry.scheme ? inquiry.scheme : `${result?.length ?? applicable.length}건`],
                    ["예상 지원금", won((result ?? applicable).reduce((s, x) => s + x.perPerson, 0) * count)],
                    ["기업정보", company ? [company.name, company.industry, company.region].filter(Boolean).join(" · ") : "비로그인 (미입력)"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "3px 0" }}>
                      <span style={{ color: c.muted }}>{k}</span>
                      <span style={{ color: c.ink, fontWeight: 600, textAlign: "right" }}>{v}</span>
                    </div>
                  ))}
                </div>

                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 6 }}>담당자 연락처</label>
                <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="휴대폰 번호 또는 이메일" style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 13px", fontSize: 14, fontFamily: "inherit", marginBottom: 16 }} />

                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 6 }}>사업자등록번호 <span style={{ color: c.muted, fontWeight: 500 }}>(대행 접수에 필요)</span></label>
                <input value={bizNo} onChange={(e) => setBizNo(e.target.value)} placeholder="000-00-00000" style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${c.actionBorder}`, borderRadius: 10, padding: "12px 13px", fontSize: 14, fontFamily: "inherit", background: "#F6F9FF" }} />
                <p style={{ fontSize: 12, color: c.muted, margin: "6px 0 16px" }}>입력하면 프로필에 저장되어 다음 문의부터 자동으로 채워집니다.</p>

                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 6 }}>희망 채용 시점</label>
                <select value={hireWhen} onChange={(e) => setHireWhen(e.target.value as (typeof HIRE_WHEN)[number])} style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 13px", fontSize: 14, fontFamily: "inherit", background: c.surface, marginBottom: 16 }}>
                  {HIRE_WHEN.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>

                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.ink, marginBottom: 6 }}>추가 요청사항 <span style={{ color: c.muted, fontWeight: 500 }}>(선택)</span></label>
                <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={3} placeholder="예: 중복 수급 가능 여부를 함께 확인하고 싶습니다" style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 13px", fontSize: 14, fontFamily: "inherit", resize: "vertical" }} />

                <div style={{ background: c.surfaceAlt, borderRadius: 10, padding: "12px 14px", margin: "16px 0 0", fontSize: 12.5, color: c.muted, lineHeight: 1.6 }}>
                  문의 내용은 제휴 노무사에게 전달되며, 영업일 1일 내 회신됩니다. 상담료·대행 수수료는 노무사가 직접 안내합니다.
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
                  <button type="button" onClick={() => setInquiry(null)} style={{ background: c.surface, color: c.muted, border: `1px solid ${c.line}`, borderRadius: 10, padding: "12px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>취소</button>
                  <button type="button" disabled={!contact.trim()} onClick={submitInquiry} style={{ background: contact.trim() ? c.action : c.line, color: "#fff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 14.5, fontWeight: 700, cursor: contact.trim() ? "pointer" : "not-allowed" }}>문의 보내기</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 탭2: 제도 카탈로그 (실데이터) ── */}
      {tab === "catalog" && usingReal && (
        <section style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 24px 64px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {realCats.map((f) => <button key={f} type="button" onClick={() => setCf(f)} style={chip(cf === f)}>{f}</button>)}
          </div>
          <p style={{ fontSize: 13, color: c.muted, margin: "0 0 16px" }}>인력·고용 지원사업 <strong style={{ color: c.actionDark }}>{realShown.length.toLocaleString()}</strong>건 (최신순)</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {realShown.map((it) => {
              const isOpen = open === `r${it.id}`;
              const lines = (it.summary ?? "").split("\n").map((l) => l.replace(/^\s*(?:[-–—•·‣▪*]\s+|\d{1,2}[.)]\s+)/, "").trim()).filter(Boolean).slice(0, 3);
              return (
                <div key={it.id} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, boxShadow: "0 2px 12px -10px rgba(14,23,38,.12)", opacity: it.closed ? 0.64 : 1 }}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : `r${it.id}`)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, background: "none", border: "none", cursor: "pointer", padding: "18px 20px", textAlign: "left" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        {it.cat2 && <span style={{ fontSize: 11.5, fontWeight: 700, color: c.actionDark, background: c.actionSoft, borderRadius: 6, padding: "3px 8px" }}>{it.cat2}</span>}
                        {it.agency && <span style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, background: c.surfaceAlt, borderRadius: 6, padding: "3px 8px" }}>{it.agency}</span>}
                        {it.region && <span style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, background: c.surfaceAlt, borderRadius: 6, padding: "3px 8px" }}>{it.region}</span>}
                        {it.closed && <span style={{ fontSize: 11.5, fontWeight: 700, color: c.danger, background: "#fdecec", borderRadius: 6, padding: "3px 8px" }}>마감</span>}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: c.ink, lineHeight: 1.35 }}>{it.title}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                      <div style={{ textAlign: "right", maxWidth: 180 }}>
                        <div className="num" style={{ fontSize: 14.5, fontWeight: 800, color: c.actionDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.amount || "공고문 참조"}</div>
                        {it.deadline && <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>~ {it.deadline}</div>}
                      </div>
                      <Icon name="expand_more" size={22} color={c.muted} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${c.line}`, marginTop: -2 }}>
                      {lines.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "16px 0 14px" }}>
                          {lines.map((l, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                              <span style={{ flexShrink: 0, width: 5, height: 5, borderRadius: "50%", background: c.actionDark, marginTop: 7 }} />
                              <span style={{ fontSize: 13.5, lineHeight: 1.55, color: c.ink }}>{l}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: 13.5, color: c.muted, margin: "16px 0 14px" }}>요약이 아직 없습니다. 공고 상세에서 원문을 확인하세요.</p>
                      )}
                      <Link href={`/programs/${it.id}` as never} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13.5, fontWeight: 700, color: c.action, textDecoration: "none" }}>
                        공고 상세 보기 <Icon name="arrow_forward" size={16} color={c.action} />
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
            {realShown.length === 0 && <p style={{ fontSize: 14, color: c.muted, padding: "30px 0", textAlign: "center" }}>해당 분류의 공고가 없습니다.</p>}
          </div>
          <p style={{ fontSize: 12.5, color: c.muted, marginTop: 18, lineHeight: 1.6 }}>제도별 지급 기준·신청 시기는 회차마다 변경될 수 있습니다. 신청 전 담당 기관의 최신 공고문을 확인하세요.</p>
        </section>
      )}

      {/* ── 탭2(폴백): 상설 제도 카탈로그 ── */}
      {tab === "catalog" && !usingReal && (
        <section style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 24px 64px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {CAT_FILTERS.map((f) => <button key={f} type="button" onClick={() => setCf(f)} style={chip(cf === f)}>{f}</button>)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {catalogShown.map((s) => {
              const isOpen = open === s.id;
              return (
                <div key={s.id} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, boxShadow: "0 2px 12px -10px rgba(14,23,38,.12)" }}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : s.id)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, background: "none", border: "none", cursor: "pointer", padding: "18px 20px", textAlign: "left" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: s.cat === "인건비 지원" ? c.actionDark : "#7c3aed", background: s.cat === "인건비 지원" ? c.actionSoft : "#f0e9ff", borderRadius: 6, padding: "3px 8px" }}>{s.cat}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, background: c.surfaceAlt, borderRadius: 6, padding: "3px 8px" }}>{s.agency}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, background: c.surfaceAlt, borderRadius: 6, padding: "3px 8px" }}>{s.regionLabel}</span>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{s.name}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                      <div style={{ textAlign: "right" }}>
                        <div className="num" style={{ fontSize: 16, fontWeight: 800, color: c.ink }}>1인 {won(s.perPerson)}</div>
                        <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{s.note}</div>
                      </div>
                      <Icon name="expand_more" size={22} color={c.muted} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${c.line}`, marginTop: -2 }}>
                      <p style={{ fontSize: 14, color: c.ink, lineHeight: 1.7, margin: "16px 0 12px" }}>{s.detail}</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {s.tags.map((t) => <span key={t} style={{ fontSize: 12, fontWeight: 600, color: c.actionDark, background: c.actionSoft, borderRadius: 999, padding: "4px 11px" }}>#{t}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 12.5, color: c.muted, marginTop: 18, lineHeight: 1.6 }}>제도별 지급 기준·신청 시기는 회차마다 변경될 수 있습니다. 신청 전 담당 기관의 최신 공고문을 확인하세요.</p>
        </section>
      )}

      <style>{`@media(max-width:820px){.jc-grid{grid-template-columns:1fr !important}}
        .jc-link-row{transition:border-color .12s ease,box-shadow .12s ease,transform .12s ease}
        .jc-link-row:hover{border-color:${c.action} !important;box-shadow:0 6px 18px -12px rgba(14,23,38,.28)}`}</style>
    </main>
  );
}
